import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Building2, FileText, LogOut, CheckCircle, Plus } from 'lucide-react';

function TradeDashboard() {
  const navigate = useNavigate();
  const { token, API, user, logout } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'trade') {
      navigate('/');
      return;
    }
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/worktops`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show worktop orders (those with pieces array)
      const worktopOrders = response.data.filter(order => 
        order.pieces && Array.isArray(order.pieces) && order.pieces.length > 0
      );
      setOrders(worktopOrders);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptQuote = async (orderId) => {
    if (!window.confirm('Accept this quote?')) return;

    try {
      await axios.post(`${API}/worktops/${orderId}/accept-quote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote accepted successfully');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to accept quote');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'quote': return 'bg-blue-100 text-blue-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const quotedOrders = orders.filter(o => o.status === 'quote');
  const acceptedOrders = orders.filter(o => o.status === 'accepted');
  const completedOrders = orders.filter(o => o.status === 'completed');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-amber-600 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white">{user?.business_name}</h1>
                <p className="text-sm text-amber-100">Trade Account Dashboard - Worktops & Hearths</p>
              </div>
            </div>
            <Button variant="outline" onClick={logout} className="bg-white text-amber-600 hover:bg-amber-50">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Request Quote Button */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/trade-quote-request')}
            className="bg-amber-600 hover:bg-amber-700 w-full md:w-auto"
            size="lg"
          >
            <Plus className="w-5 h-5 mr-2" />
            Request a New Quote
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Pending Quotes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{quotedOrders.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">In Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{acceptedOrders.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-600">{completedOrders.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Quoted Orders */}
        {quotedOrders.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Pending Quotes</h2>
            <div className="space-y-4">
              {quotedOrders.map(order => (
                <Card key={order.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">Order #{order.order_number}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {order.status.toUpperCase()}
                          </span>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            {order.order_type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {order.pieces.length} piece{order.pieces.length !== 1 ? 's' : ''} • 
                          Created {new Date(order.created_at).toLocaleDateString()}
                        </p>
                        <div className="text-2xl font-bold text-gray-900 mt-2">£{order.total.toFixed(2)}</div>
                        <p className="text-sm text-gray-500">inc. VAT</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => navigate(`/worktops/${order.id}`)}
                          variant="outline"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          View Details
                        </Button>
                        <Button
                          onClick={() => handleAcceptQuote(order.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Accept Quote
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Accepted Orders */}
        {acceptedOrders.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">In Progress</h2>
            <div className="space-y-4">
              {acceptedOrders.map(order => (
                <Card key={order.id} onClick={() => navigate(`/trade-order/${order.id}`)} className="cursor-pointer hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">Order #{order.order_number}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            IN PROGRESS
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {order.pieces.length} piece{order.pieces.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-gray-900">£{order.total.toFixed(2)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Completed Orders */}
        {completedOrders.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Completed</h2>
            <div className="space-y-4">
              {completedOrders.map(order => (
                <Card key={order.id} onClick={() => navigate(`/trade-order/${order.id}`)} className="cursor-pointer hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">Order #{order.order_number}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            COMPLETED
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {order.pieces.length} piece{order.pieces.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-gray-900">£{order.total.toFixed(2)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {orders.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No Worktop Orders Yet</h3>
              <p className="text-sm text-gray-500 mb-4">Click "Request a New Quote" above to get started with your first worktop order</p>
              <Button
                onClick={() => navigate('/trade-quote-request')}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Request Your First Quote
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default TradeDashboard;
